import React from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, LinearProgress, Box } from '@mui/material';
import AnnualPlanning from './web/app/redux/components/AnnualPlanning/AnnualPlanning';
import NewAnnualPlan from './web/app/redux/components/AnnualPlanning/FirstTerm/NewAnnualPlan';
import DefineObjectives from './web/app/redux/components/AnnualPlanning/FirstTerm/DefineObjectives';
import DefineActivities from './web/app/redux/components/AnnualPlanning/FirstTerm/DefineActivities';
import ApprovalDefinitions from './web/app/redux/components/AnnualPlanning/FirstTerm/ApprovalDefinitions';
import EnterCompliance from './web/app/redux/components/AnnualPlanning/SecondTerm/EnterCompliance';
import CEOValidation from './web/app/redux/components/AnnualPlanning/SecondTerm/CEOValidation';
import Checklist from './web/app/redux/components/AnnualPlanning/SecondTerm/Checklist';
import SelfAssessmentResults from './web/app/redux/components/AnnualPlanning/SecondTerm/SelfAssessmentResults';
import DataSourceService from './core/services/dataSource';
import createStore from './core/store';
import SnackAlert from './web/app/redux/components/Layout/SnackAlert';
import CookieReportModal from './web/app/redux/components/CookieReportModal';
import { useAnyLoading } from './core/useAnyLoading';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      dark: '#1565c0',
    },
    secondary: {
      main: '#388e3c',
    },
    error: {
      main: '#d32f2f',
    },
  },
  typography: {
    fontFamily: "'Source Sans 3', sans-serif",
  },
});

const dataSource = DataSourceService({
  baseUrl: '',
});

const store = createStore({
  dataSource,
});

const AppRoutes: React.FC = () => {
  const location = useLocation();
  const [openCookieModal, setOpenCookieModal] = React.useState(false);
  const isAnyLoading = useAnyLoading({ enterDelay: 120, exitDelay: 450 });

  React.useEffect(() => {
    const mainPaths = ['/', '/annual-plan'];
    if (mainPaths.includes(location.pathname)) {
      setOpenCookieModal(false);
    } else {
      setOpenCookieModal(false);
    }
  }, [location.pathname]);

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          pointerEvents: 'none',
        }}
      >
        <LinearProgress
          color="primary"
          sx={{
            opacity: isAnyLoading ? 1 : 0,
            transform: isAnyLoading ? 'scaleY(1)' : 'scaleY(0.35)',
            transformOrigin: 'top',
            transition: 'opacity 0.35s ease, transform 0.35s ease',
            height: 3,
            willChange: 'opacity, transform',
          }}
        />
      </Box>
      <SnackAlert>
        <Routes>
          <Route path="/" element={<AnnualPlanning />} />
          <Route path="/annual-plan" element={<AnnualPlanning />} />
          <Route path="/new-annual-plan" element={<NewAnnualPlan />} />
          <Route path="/define-objectives" element={<DefineObjectives />} />
          <Route path="/define-activities" element={<DefineActivities />} />
          <Route path="/approval-definitions" element={<ApprovalDefinitions />} />
          <Route path="/enter-compliance" element={<EnterCompliance />} />
          <Route path="/checklist" element={<Checklist />} />
          <Route path="/ceo-validation" element={<CEOValidation />} />
          <Route path="/self-assessment-results" element={<SelfAssessmentResults />} />
        </Routes>
        <CookieReportModal open={openCookieModal} onClose={() => setOpenCookieModal(false)} />
      </SnackAlert>
    </>
  );
};

const App: React.FC = () => (
  <Provider store={store}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppRoutes />
      </Router>
    </ThemeProvider>
  </Provider>
);

export default App;
